// ✅ FooterMenu.jsx

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash.get';
import DOMPurify from 'isomorphic-dompurify';
import imgSearchMore from '../../../static/image/ic_search_more.svg';

import MediaView from '../../containers/MediaView';
import storage from '../../../lib/storage';
import { getTodayCollections } from '../../../store/reducers/collection';
import translations from '../../../utils/translations';
import { flexCenter } from '../../../utils/mixins';
import { getMenuImage } from './menuIcons';

const Wrapper = styled.div`
  width: 100%;
  position: absolute;
`;

const StyledMenu = styled.div`
  width: 100%;
  height: 70px;
  border-radius: 50px 50px 0 0;
  position: fixed;
  bottom: 0;
  background: ${(props) => props.color};
  box-shadow: 0 -2px 4px rgb(0 0 0 / 5%);
  z-index: 99;
  ${flexCenter};

  .menu {
    color: #a0a4a8;
  }

  .menu.active {
    color: ${(props) => props.fontColor};
    font-weight: 600;

    @media (min-width: 640px) {
      background: #f4f4f4;
    }
  }

  .inner-block {
    width: 100%;
    display: flex;
    margin: 0 5px;
  }

  @media (min-width: 640px) {
    height: 80px;
    position: absolute;

    .inner-block {
      width: 414px;
      height: 80px;
    }
  }
`;

const MenuLink = styled(NavLink)`
  width: 20%;
  margin: 0 auto;
  font-size: 8px;
  ${flexCenter}
  flex-direction: column;

  span {
    margin-top: -2px;
  }

  .inline {
    text-align: center;

    img {
      padding-bottom: 1px;
    }
  }

  @media (min-width: 640px) {
    width: 83px;

    :hover {
      background: #f4f4f4;
    }
  }
`;

const Footer = styled.div`
  width: 100%;
  padding: 60px 0;
  background-color: #222;
  color: #fff;
  font-weight: 500;
  ${flexCenter}
  text-align: center;
  .text-block {
    font-size: 14px;
    letter-spacing: -0.5px;
    width: 500px;
    .divider {
      padding: 0 15px;
    }

    .title {
      margin-right: 39px;
      font-size: 12px;
      font-weight: 500;
    }

    .text1 {
      padding-bottom: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .arrow-block {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
    }

    .arrow {
      transition: transform 0.2s ease;
      width: 18px;
      height: 18px;
    }

    .arrow.rotate {
      transform: rotate(180deg);
    }

    .contact1 {
      margin-bottom: 15px;
      line-height: 20px;
    }

    .contact2 {
      display: inline-flex;
      margin-bottom: 15px;
    }
  }

  .span {
    margin-left: 60px;
  }
`;

const CopyRight = styled.div`
  width: 100%;
  height: 45px;
  background: black;
  color: white;
  font-size: 12px;
  ${flexCenter};
  font-weight: 300;
`;

const FooterMenu = () => {
  const dispatch = useDispatch();
  const [showCompany, setShowCompany] = useState(false);
  const toggleShowCompany = () => setShowCompany((v) => !v);
  const location = useLocation();
  const [pathName, setPathName] = useState('/');
  const userLanguage = useSelector((state) => get(state, ['auth', 'userLanguage']));
  const isDarkMode = pathName.indexOf('/home/collection') === 0;

  useEffect(() => {
    setPathName(location.pathname);
  }, [location]);

  const initScrollPosition = () => {
    storage.set('lastScrollPosition', 0);
    storage.set('lastLoadedSize', 0);
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
    // dispatch(getTodayCollections());
  };

  if (window.isNativeApp) return null;

  const menuItems = [
    {
      key: 'home',
      to: '/',
      isActive: () => pathName === '/',
      label: translations[userLanguage].menu_home,
    },

    {
      key: 'youbooker',
      to: '/member',
      isActive: () => pathName === '/member',
      label: 'S교수',
    },

    {
      key: 'together',
      to: '/mentoring',
      isActive: () => ['/mentoring', '/suggested', '/scheduled', '/together'].some((p) => pathName.indexOf(p) === 0),
      label: translations[userLanguage].menu_together,
    },

    {
      key: 'memo',
      to: '/memo/public',
      isActive: () => pathName.indexOf('/memo/public') === 0,
      label: translations[userLanguage].menu_memo,
    },
    {
      key: 'book',
      to: '/home/collection',
      isActive: () => (pathName.indexOf('/producer') === 0 && pathName.indexOf('/myProfile') === 0)
        || pathName.indexOf('/collection') === 0
        || pathName.indexOf('/home/collection') === 0
        || pathName.indexOf('/dailyIssue') === 0,
      label: translations[userLanguage].menu_book,
    },
  ];

  return (
    <Wrapper>
      <MediaView maxWidth={639}>
        <StyledMenu
          color={isDarkMode ? 'black' : 'white'}
          fontColor={isDarkMode ? '#5D9DF0' : 'black'}
        >
          <div className="inner-block">
            {menuItems.map(({ key, to, isActive, label }) => (
              <MenuLink
                key={key}
                to={to}
                onClick={initScrollPosition}
                className="menu"
                activeClassName="active"
                isActive={isActive}
              >
                <div className="inline">
                  <img src={getMenuImage(key, isActive(), isDarkMode)} alt={label} />
                  <div>{label}</div>
                </div>
              </MenuLink>
            ))}
          </div>
        </StyledMenu>
      </MediaView>

      <MediaView minWidth={640}>
        <Footer>
          <div className="text-block">
            <div className="text1">
              사업자 정보
              <div
                className="arrow-block"
                onClick={() => { toggleShowCompany(); }}
              >
                <img
                  src={imgSearchMore}
                  alt="arrow"
                  className={showCompany ? 'arrow rotate' : 'arrow'}
                />
              </div>
            </div>
            {showCompany && (
              <div>
              <div className="text1">{translations[userLanguage].home_book_company_info}</div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(translations[userLanguage].home_page_about_detail),
                  }}
                />
                <br /><br />
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => { window.open('https://academy-connect.website/termsofservice/', '_blank'); }}
                >
                  {translations[userLanguage].terms_title}
                </span>
                <span className="divider">|</span>
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => { window.open('https://academy-connect.website/privacypolicy/', '_blank'); }}
                >
                  {translations[userLanguage].privacy_title}
                </span>
              </div>
            )}
          </div>
        </Footer>
        <CopyRight>
          Copyright © 2022 NICK syndicate. All rights reserved.
        </CopyRight>
      </MediaView>
    </Wrapper>
  );
};

export default FooterMenu;
